import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VisibilityReportResponse } from '@brand24-original/llm-types';
import {
  downloadSharedVisibilityReportPdf,
  downloadVisibilityReportPdf,
} from '../../services/visibility-report/VisibilityReportService';
import { downloadBlobAsFile } from '../../../utils/utils';

/**
 * Downloads the report PDF the backend renders from the stored snapshot — the same file the panel,
 * the shared link and `/v2` hand out. With `shareToken` it goes through the public link endpoint.
 * The PDF's language follows the panel's (Polish for `pl*`, English otherwise).
 */
export const useDownloadVisibilityReportPdf = (
  report: VisibilityReportResponse | undefined,
  shareToken?: string
) => {
  const { i18n } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!report || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const lang = i18n.language.startsWith('pl') ? 'pl' : 'en';
      const blob = shareToken
        ? await downloadSharedVisibilityReportPdf(shareToken, lang)
        : await downloadVisibilityReportPdf(report.id, lang);
      // The date keeps browsers from appending "(1)" and an older file in the download history from
      // passing for a fresh export.
      downloadBlobAsFile(
        blob,
        `chatbeat-visibility-report-${report.domain}-${report.generatedAt.slice(0, 10)}.pdf`
      );
    } finally {
      setIsDownloading(false);
    }
  }, [report, shareToken, isDownloading, i18n.language]);

  return { download, isDownloading };
};
